import { isEmpty } from 'lodash'
import { Suspense, useCallback } from 'react'
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom'

import LoadingWithBackdrop from '~/components/loadingWithBackdrop'
import PageErrorBoundary from '~/components/pageErrorBoundary'
import AgentInfoHeader from '~/features/agents/components/agentInfoHeader'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import AsyncExportJobsCoordinator from '~/features/exports/components/asyncExportJobsCoordinator'
import FirstAssistantOnboarding from '~/features/onboarding/components/firstAssistantOnboarding'
import { isRouteAccessibleWithoutAssistants } from '~/features/onboarding/constants'
import useHasVoipOrgFeature from '~/features/organizations/hooks/useHasVoipOrgFeature'
import useUpdateOrganizationStore from '~/features/organizations/hooks/useUpdateOrganizationStore'
import PartnerThemeWrapper from '~/features/partners/components/partnerThemeWrapper'
import { CREATE_SCENARIO_ACTION_ROUTE_ID } from '~/features/scenarios/constants'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import usePrivateRoutesRedirect from '~/hooks/usePrivateRoutesRedirect'
import useRedirectOnEmptyStore from '~/hooks/useRedirectOnEmptyStore'
import ContentWrapper from '~/layouts/account/contentWrapper'
import ScenariosLayout from '~/layouts/account/scenariosLayout'
import SettingsLayout from '~/layouts/account/settingsLayout'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import { sessionStore } from '~/store'
import pathBuilder from '~/utils/api'

function AccountLayout() {
  useRedirectOnEmptyStore()
  const {
    agents,
    isLoading: isLoadingGettingAgents,
    selectedAgent,
  } = useGetAgentCentralized()
  const isLoadingOrganization = useUpdateOrganizationStore()
  const isAuthCheckComplete = usePrivateRoutesRedirect({
    requiredUserGroups: null,
  })
  const { pathname } = useLocation()
  const isSettingsPage = pathname.includes(EClientPath.settingsBase)
  const isScenariosPage =
    pathname.includes(EClientPath.scenariosBase) &&
    import.meta.env[EEnvironmentVariables.VITE_IS_SCENARIOS_ENABLED] === 'true'
  const isManageIntegrationsPage = pathname.includes(
    EClientPath.manageIntegrations,
  )

  const {
    isPlusUser,
    isCustomAccess,
    canAccessOrgConfig,
    canAccessCrm,
    canAccessReporting,
    canAccessCampaigns,
    canManageUsers,
    canAccessAiAssistantConfig,
    canAccessKnowledgeBase,
    canAccessVoipConfig,
    canAccessVoipVoicemails,
    isLoadingPermissions,
  } = useUserAccess()
  const { canEdit, canRead } = useCapabilities()
  const canEditCampaigns = canEdit(ECapability.campaigns)
  const canEditAiAssistantConfig = canEdit(ECapability.aiAssistantConfig)
  const canReadAiAssistantConfig = canRead(ECapability.aiAssistantConfig)

  const hasVoipOrgFeature = useHasVoipOrgFeature()

  const isCustomAccessPathAllowed = useCallback(
    (path: string) => {
      const isScenariosFeatureEnabled =
        import.meta.env[EEnvironmentVariables.VITE_IS_SCENARIOS_ENABLED] ===
        'true'
      const patterns: Array<{ path: string; end?: boolean }> = [
        { path: EClientPath.overview, end: true },
      ]

      if (canManageUsers) {
        patterns.push({ path: EClientPath.users, end: false })
      }

      if (canAccessOrgConfig) {
        patterns.push(
          { path: EClientPath.manageBrand, end: true },
          { path: EClientPath.manageBrandCreate, end: true },
          { path: EClientPath.manageBrandDetail, end: false },
          { path: EClientPath.account, end: true },
          { path: EClientPath.billing, end: true },
          { path: EClientPath.plan, end: true },
          { path: EClientPath.manageIntegrations, end: false },
          { path: EClientPath.settingsApiKeys, end: true },
        )
      }

      if (canAccessCrm) {
        patterns.push(
          { path: EClientPath.inboxMessage, end: false },
          { path: EClientPath.contacts, end: true },
          { path: EClientPath.contact, end: false },
        )
      }

      if (canAccessReporting) {
        patterns.push({ path: EClientPath.reports, end: false })
      }

      if (canAccessCampaigns) {
        patterns.push(
          { path: EClientPath.campaignList, end: true },
          { path: EClientPath.campaignOfuManage, end: false },
        )
      }

      if (canEditCampaigns) {
        patterns.push(
          { path: EClientPath.campaignTemplates, end: true },
          { path: EClientPath.campaignSelection, end: true },
          { path: EClientPath.campaignBuilder, end: false },
        )
      }

      if (canAccessAiAssistantConfig) {
        patterns.push(
          { path: EClientPath.settingsCoreBehavior, end: true },
          { path: EClientPath.manageAssistants, end: true },
          { path: EClientPath.settingsChatbot, end: true },
          { path: EClientPath.settingsEmail, end: true },
          { path: EClientPath.settingsText, end: true },
          { path: EClientPath.settingsPhone, end: true },
          { path: EClientPath.settingsNotifications, end: true },
          { path: EClientPath.settingsAnalysisSettingsV2, end: true },
        )
        if (
          import.meta.env[
            EEnvironmentVariables.VITE_IS_LIVE_TRANSFERS_ENABLED
          ] === 'true'
        ) {
          patterns.push({
            path: EClientPath.liveTransfersScenarios,
            end: false,
          })
        }
        if (isScenariosFeatureEnabled) {
          patterns.push(
            { path: EClientPath.manageScenariosV2, end: true },
            { path: EClientPath.manageScenariosActions, end: true },
          )
        }
      }

      if (canReadAiAssistantConfig && isScenariosFeatureEnabled) {
        patterns.push({ path: EClientPath.scenarioCreateV2Edit, end: false })
      }

      if (canAccessKnowledgeBase) {
        patterns.push(
          { path: EClientPath.settingsKnowledge, end: true },
          { path: EClientPath.settingsIndividualKnowledgeSet, end: false },
        )
      }

      if (canEditAiAssistantConfig && isScenariosFeatureEnabled) {
        patterns.push(
          { path: EClientPath.customScenarioV2Create, end: true },
          { path: EClientPath.scenarioActionV2CreateEdit, end: false },
        )
      }

      if (hasVoipOrgFeature && canAccessVoipConfig) {
        patterns.push(
          { path: EClientPath.voipPhoneNumbers, end: true },
          { path: EClientPath.voipDevices, end: true },
          { path: EClientPath.voipPhoneNumberDetail, end: false },
        )
      }

      if (hasVoipOrgFeature && canAccessVoipVoicemails) {
        patterns.push({ path: EClientPath.voipCalls, end: false })
      }

      const isCreateActionPath = matchPath(
        {
          path: pathBuilder(EClientPath.scenarioActionV2CreateEdit, {
            actionId: CREATE_SCENARIO_ACTION_ROUTE_ID,
          }),
          end: true,
        },
        path,
      )
      const isActionDetailPath =
        !isCreateActionPath &&
        matchPath(
          { path: EClientPath.scenarioActionV2CreateEdit, end: false },
          path,
        )
      const canAccessActionDetail =
        isScenariosFeatureEnabled &&
        canReadAiAssistantConfig &&
        isActionDetailPath

      return (
        patterns.some((spec) =>
          matchPath({ path: spec.path, end: spec.end ?? false }, path),
        ) || !!canAccessActionDetail
      )
    },
    [
      canAccessCampaigns,
      canAccessCrm,
      canAccessOrgConfig,
      canAccessReporting,
      canEditAiAssistantConfig,
      canEditCampaigns,
      canManageUsers,
      canAccessAiAssistantConfig,
      canAccessKnowledgeBase,
      canAccessVoipConfig,
      canAccessVoipVoicemails,
      canReadAiAssistantConfig,
      hasVoipOrgFeature,
    ],
  )

  const [agentId, isLoadingUser] = sessionStore((state) => [
    state.agent.id,
    state.UIElements.isLoadingUser,
  ])

  const isLoading = isLoadingOrganization || isLoadingGettingAgents

  if (!isAuthCheckComplete || isLoadingPermissions) {
    return <LoadingWithBackdrop show />
  }

  if (isCustomAccess && !isCustomAccessPathAllowed(pathname)) {
    return <Navigate to={EClientPath.overview} replace />
  }

  const hasAgents = !isEmpty(agents)
  const hasSelectedAgent = !!selectedAgent?.[EAgentKeys.id]
  const isRouteAccessible = isRouteAccessibleWithoutAssistants(pathname)
  const isWrongAgent = !!agentId && !hasSelectedAgent
  const allAgentsAreDisabled = hasAgents && !hasSelectedAgent
  const isAccessible = hasSelectedAgent || isRouteAccessible

  const canViewOrgConfigRouteWithoutAgent =
    isCustomAccess && canAccessOrgConfig && isCustomAccessPathAllowed(pathname)

  const showFirstAssistantOnboarding =
    !isLoading &&
    !isCustomAccess &&
    !isRouteAccessible &&
    (!hasAgents || isWrongAgent || allAgentsAreDisabled)

  let noPadding = false

  const staticRoutesWithoutPadding = [
    EClientPath.contacts,
    EClientPath.settingsKnowledge,
    EClientPath.settingsChatbot,
    EClientPath.settingsEmail,
    EClientPath.liveTransfersScenarios,
    EClientPath.campaignList,
    EClientPath.settingsText,
    EClientPath.settingsPhone,
    EClientPath.campaignTemplates,
    EClientPath.billing,
    EClientPath.users,
    EClientPath.manageAssistants,
    EClientPath.settingsPostConvoSettings,
    EClientPath.settingsAnalysisSettingsV2,
    EClientPath.settingsCoreBehavior,
    EClientPath.settingsNotifications,
    EClientPath.referralsInfo,
    EClientPath.voipPhoneNumbers,
    EClientPath.voipDevices,
    EClientPath.voipCalls,
  ]

  const dynamicRoutesWithoutPadding = [
    EClientPath.contact,
    EClientPath.manageBrand,
    EClientPath.campaignBuilder,
    EClientPath.campaignOfuManage,
    EClientPath.voipPhoneNumberDetail,
    EClientPath.reports,
    EClientPath.settingsIndividualKnowledgeSet,
    EClientPath.liveTransferCreateEdit,
  ]

  if (pathname.includes(EClientPath.scenariosBase)) {
    noPadding = isPlusUser && !showFirstAssistantOnboarding
  } else if (pathname.includes(EClientPath.manageIntegrations)) {
    noPadding = true
  } else if (pathname.includes(EClientPath.inbox)) {
    noPadding = !showFirstAssistantOnboarding
  } else if (
    dynamicRoutesWithoutPadding.some((pattern) =>
      matchPath({ path: pattern, end: false }, pathname),
    )
  ) {
    noPadding = true
  } else if (staticRoutesWithoutPadding.some((path) => path === pathname)) {
    noPadding = true
  }

  const showSettingsPage =
    !showFirstAssistantOnboarding &&
    !isLoading &&
    isSettingsPage &&
    (isAccessible || canViewOrgConfigRouteWithoutAgent)

  const showScenariosPage =
    !showFirstAssistantOnboarding &&
    !isLoading &&
    isScenariosPage &&
    isAccessible

  const showManageIntegrationsPage =
    !isLoading &&
    isManageIntegrationsPage &&
    (isAccessible || canViewOrgConfigRouteWithoutAgent)

  const showDefaultPage =
    !showFirstAssistantOnboarding &&
    !isManageIntegrationsPage &&
    !isLoading &&
    !isScenariosPage &&
    !isSettingsPage &&
    (isAccessible || isCustomAccess)

  const isAdminPortalRoute = pathname.includes(EAdminPortalClientPath.admin)

  return (
    <Suspense fallback={<LoadingWithBackdrop show />}>
      <PartnerThemeWrapper>
        <AsyncExportJobsCoordinator />
        {/* Don't show loading indicator on settings page as it has its own loading indicator. */}
        <ContentWrapper
          contentHeader={<AgentInfoHeader />}
          isLoading={
            (!isSettingsPage && isLoading) ||
            isLoadingGettingAgents ||
            isLoadingUser
          }
          noPadding={noPadding}
          showContactInfoButton={
            !isAdminPortalRoute && canRead(ECapability.aiAssistantConfig)
          }
        >
          {showFirstAssistantOnboarding && <FirstAssistantOnboarding />}
          {showSettingsPage && (
            <PageErrorBoundary>
              <SettingsLayout>
                <Outlet />
              </SettingsLayout>
            </PageErrorBoundary>
          )}
          {showScenariosPage && (
            <PageErrorBoundary>
              <ScenariosLayout>
                <Outlet />
              </ScenariosLayout>
            </PageErrorBoundary>
          )}
          {showManageIntegrationsPage && (
            <PageErrorBoundary>
              <Outlet />
            </PageErrorBoundary>
          )}
          {showDefaultPage && (
            <PageErrorBoundary>
              <Outlet />
            </PageErrorBoundary>
          )}
        </ContentWrapper>
      </PartnerThemeWrapper>
    </Suspense>
  )
}

export default AccountLayout
